import React, { memo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { CloseIcon } from '../common/icons/closeIcon';
import { translate } from '../../common/translation/translate';
import type { IStoreState } from '../common/interfaces/types';
import { useInterval } from '../../common/hooks/useInterval';
import { reduxConnect } from '../../common';
import { dataLayer } from '@weatherbug/common';
import { CONDITION_DETAIL_URL, DEFAULT_OBS_INTERVAL, REFRESH, VERBOSE } from './constants';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';

interface CurrentConditionDisplayProps {
    authId: string;
    baseUrl: string;
    secret: string;
    stdParameters: Record<string, unknown>;
    isMapLayerPickerClosed: boolean;
    nowConditionsDescription: string;
    getNowConditions: (parameters: any) => void;
    latitude: number;
    longitude: number;
    displayCompositeName: string;
    isMobile: boolean;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { nowConditions, appState } = storeState || {};
    const { authId, secret, obsBaseUrl, stdParameters, user } = appState?.appState || {};
    const { d } = nowConditions?.nowConditions || {};

    const { Locality } = user?.ActiveLocation ?? {};
    const { Latitude, Longitude, DisplayCompositeName } = Locality ?? {};

    return {
        nowConditionsDescription: d,
        authId,
        secret,
        baseUrl: obsBaseUrl,
        stdParameters,
        latitude: Latitude,
        longitude: Longitude,
        displayCompositeName: DisplayCompositeName
    };
};

const mapDispatchToProps = {
    getNowConditions: dataLayer?.actions?.getNowConditions
};

const CurrentConditionDisplayComponent = ({
    authId,
    secret,
    baseUrl,
    stdParameters,
    latitude,
    longitude,
    isMapLayerPickerClosed,
    nowConditionsDescription,
    getNowConditions,
    displayCompositeName,
    isMobile
}: CurrentConditionDisplayProps) => {
    const [isVisible, setIsVisible] = useState(true);
    const mapLayerPickerHeight = isMapLayerPickerClosed ? '0px' : '295px';
    const headerText = translate('currentCondition');

    useEffect(() => {
        if (isMobile) {
            setIsVisible(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (isVisible && !isMobile) {
            getNowConditions({
                latitude,
                longitude,
                baseUrl,
                authId,
                secret,
                stdParameters,
                VERBOSE,
                REFRESH
            });
        }
    }, [isVisible, isMobile, latitude, longitude, authId, secret, baseUrl, getNowConditions]);

    const handleClose = () => {
        setIsVisible(false);
    };

    return isVisible
        ? <Container mapLayerPickerHeight={mapLayerPickerHeight} role='complementary'>
            <ContentWrapper>
                <Header aria-label={`${headerText} in ${displayCompositeName}`}>
                    {headerText}
                </Header>
                <TextContent href={CONDITION_DETAIL_URL}>{nowConditionsDescription}</TextContent>
            </ContentWrapper>
            <CloseButton onClick={handleClose} aria-label='Close current condition display'>
                <CloseIcon />
            </CloseButton>
        </Container>
        : null;
};

export const CurrentConditionDisplay = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(withScreenSize(CurrentConditionDisplayComponent)));

CurrentConditionDisplay.displayName = 'CurrentConditionDisplay';

interface ContainerProps {
    mapLayerPickerHeight: string;
}

const Container = styled.div<ContainerProps>`
    display: flex;
    position: absolute;
    bottom: ${props => `calc(348px + ${props.mapLayerPickerHeight})`};
    right: 56px;
    width: 238px;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    background-color: #FFFFFF;
    gap: 4px;
    padding: 10px 15px;

    @media (max-width: 720px) {
        bottom: ${props => `calc(159px + ${props.mapLayerPickerHeight})`};
    }
`;

const CloseButton = styled.button`
    display: block;
    min-height: 24px;
    min-width: 24px;
    background: none;
    border: none;
    outline: none;
    padding: 0;
    margin: 0;
`;

const ContentWrapper = styled.section`
    display: flex;
    flex-direction: column;
    text-align: left;
    gap: 4px;
`;

const Header = styled.h2`
    margin: 0;
    padding: 0;
    font-weight: 400;
    font: 14px/18px "Source Sans Pro", sans-serif;
    color: #555555;
`;

const TextContent = styled.a`
    font-weight: 700;
    font: 14px/18px "Source Sans Pro", sans-serif;
    color: #007AFF;
    width: 177px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
